import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";

import HttpManager from "../../../services/HttpManager";
import { FuenteDetectada, ProspectCatalogs } from "../../../services/v2/prospects.service";
import { CursosCompacto } from "./cursosSelect";
import { ETAPA_BORDE, ST_PILL } from "./crmLista";
import { cuando, errorTexto, ETAPAS, nombreCompleto } from "./salesHelpers";
import { telefonoBonito } from "../../../utils/telefonoBonito";
import { PHONE_COUNTRIES } from "../../../constants/phonePrefixes";

interface Props {
	campusId: number;
	catalogs: ProspectCatalogs | null;
	onClose: () => void;
	onCreated: (id: number) => void;
	onOpenExisting: (id: number) => void;
	/** Desde Nuevo Chat: el teléfono ya escrito, con lada (+52…, +504…). */
	telefonoInicial?: string;
}

/** Lada por país (como lo guarda WhatsApp/Woztell): México y Honduras primero. */
const LADAS: { k: string; t: string; pais: number }[] = [
	{ k: "52", t: "MX +52", pais: 1 }, { k: "504", t: "HN +504", pais: 2 }, { k: "503", t: "SV +503", pais: 60 },
	{ k: "1", t: "EE. UU. +1", pais: 66 }, { k: "502", t: "GT +502", pais: 79 }, { k: "505", t: "NI +505", pais: 0 }, { k: "506", t: "CR +506", pais: 0 },
	{ k: "34", t: "ES +34", pais: 65 },
];
/** Los demás países (2026-10-21: no se podía dar de alta a una clienta de España). Una lada por opción. */
const OTRAS_LADAS = PHONE_COUNTRIES.filter((c, i, todos) => !LADAS.some((l) => l.k === c.prefix) && todos.findIndex((x) => x.prefix === c.prefix) === i)
	.sort((a, b) => a.name.localeCompare(b.name, "es"));
const PAIS_DE_PLANTEL: { [id: number]: number } = { 1: 1, 2: 1, 3: 2, 4: 2 };
/** Status con los que puede empezar alguien nuevo (los que sacan de seguimiento no aplican al dar de alta). */
const STATUS_INICIAL = ["nuevo_cliente", "interesado", "promesa", "potencial"];
/** Orden de las fuentes: lo que más entra primero. */
const ORDEN_FUENTE = ["facebook", "instagram", "tiktok", "whatsapp", "web", "referido", "presencial", "llamada", "google", "bombardeo", "otro"];
/** Qué se pregunta según la fuente (una línea, sin explicar). */
const DETALLE: { [code: string]: { campo: "adName" | "detalle"; label: string; ph: string } } = {
	facebook: { campo: "adName", label: "Anuncio", ph: "Opcional" }, instagram: { campo: "adName", label: "Anuncio", ph: "Opcional" },
	tiktok: { campo: "adName", label: "Video", ph: "Opcional" }, google: { campo: "adName", label: "Anuncio", ph: "Opcional" },
	referido: { campo: "detalle", label: "¿Quién la refirió?", ph: "Nombre o teléfono" }, otro: { campo: "detalle", label: "¿Cuál?", ph: "" },
};

/**
 * Nuevo Prospecto (rediseño 2026-10-09, Esteban: "está terrible y fuera de ritmo ... ni deja seleccionar en qué estatus
 * está para comenzar"). Empieza por el teléfono con lada: si ya existe se ofrece su ficha; si ya escribió a un número de
 * Woztell, la fuente y el número se llenan solos. Luego etapa, status, curso, plantel, dueña y fuente.
 */
const NewProspectModal: React.FC<Props> = ({ campusId, catalogs, onClose, onCreated, onOpenExisting, telefonoInicial }) => {
	const http = HttpManager.getInstance();
	const paisPlantel = PAIS_DE_PLANTEL[campusId] || 2;
	const [lada, setLada] = useState<string>(LADAS.find((l) => l.pais === paisPlantel)?.k || "504");
	// Con "+" el número se respeta tal cual (la lada del select no se le vuelve a poner).
	const [num, setNum] = useState<string>(telefonoInicial || "");
	const [lookup, setLookup] = useState<{ valid: boolean; phone: string | null; match: any; detectado?: FuenteDetectada | null } | null>(null);
	const [buscando, setBuscando] = useState<boolean>(false);
	const [f, setF] = useState({ firstName: "", lastName: "", courseInterest: "", campusId: String(campusId), etapaId: 1, statusId: 1, potencialMotivo: "",
		ownerZohoId: "", sourceId: 0, channelId: "", adName: "", detalle: "", note: "" });
	const [auto, setAuto] = useState<boolean>(false);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const timer = useRef<any>(null);
	const set = (k: keyof typeof f, v: any) => setF((x) => ({ ...x, [k]: v }));

	// Lo que se manda: con "+" se respeta lo que escribió; si no, la lada elegida + los dígitos.
	const telefono = useMemo(() => {
		const d = num.replace(/\D/g, "");
		if (!d) return "";
		if (num.trim().startsWith("+")) return "+" + d;
		return "+" + lada + (d.startsWith(lada) && d.length > lada.length + 7 ? d.slice(lada.length) : d);
	}, [num, lada]);

	useEffect(() => {
		clearTimeout(timer.current);
		if (num.replace(/\D/g, "").length < 8) { setLookup(null); setBuscando(false); return; }
		setBuscando(true);
		timer.current = setTimeout(async () => {
			try {
				const r = (await http.prospectsService.lookupPhone(campusId, telefono)).data;
				setLookup(r);
				const d = r.detectado;
				if (d && !r.match) {
					// Ya escribió por WhatsApp (lo vio la copia de Woztell): fuente, número y anuncio se llenan solos.
					setF((x) => ({ ...x, sourceId: d.sourceId || x.sourceId, channelId: d.channelId ? String(d.channelId) : x.channelId,
						adName: x.adName || d.adName || "", campusId: d.campusId ? String(d.campusId) : x.campusId }));
					setAuto(true);
				} else {
					setAuto(false);
				}
			} catch (e) { setLookup(null); }
			finally { setBuscando(false); }
		}, 350);
		return () => clearTimeout(timer.current);
	}, [telefono]);

	const statuses = (catalogs?.statuses || []).filter((s) => STATUS_INICIAL.includes(s.code));
	const st = catalogs?.statuses.find((s) => s.id === f.statusId);
	const fuentes = [...(catalogs?.sources || [])].sort((a, b) => (ORDEN_FUENTE.indexOf(a.code) + 99) % 99 - (ORDEN_FUENTE.indexOf(b.code) + 99) % 99);
	const fuente = catalogs?.sources.find((s) => s.id === f.sourceId);
	const det = fuente ? DETALLE[fuente.code] : undefined;
	const paisElegido = PAIS_DE_PLANTEL[Number(f.campusId)] || 0;
	const canales = (catalogs?.canales || []).filter((c) => !paisElegido || c.country_id === paisElegido);
	const esSup = !!catalogs?.isSupervisor;
	const abierto = !!lookup?.valid && !lookup.match;
	const falta = !abierto ? "el teléfono" : !f.firstName.trim() ? "el nombre" : !f.courseInterest.trim() ? "el curso" : !f.campusId ? "el plantel"
		: st?.bool_needs_reason && !f.potencialMotivo ? "el motivo" : null;

	const guardar = async () => {
		if (falta || busy) return;
		setBusy(true); setError(null);
		try {
			const r = await http.prospectsService.create(campusId, {
				phone: telefono, firstName: f.firstName, lastName: f.lastName, courseInterest: f.courseInterest, campusId: Number(f.campusId),
				etapaId: f.etapaId, statusId: f.statusId, potencialMotivo: st?.bool_needs_reason ? f.potencialMotivo : null,
				ownerZohoId: f.ownerZohoId || null, sourceId: f.sourceId || null, channelId: f.channelId ? Number(f.channelId) : null,
				adName: det?.campo === "adName" ? f.adName : (auto ? f.adName : ""), detalle: det?.campo === "detalle" ? f.detalle : null, note: f.note,
			});
			onCreated(r.data.prospect.id);
		} catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); }
	};

	const m = lookup?.match;
	const d = lookup?.detectado;
	return (
		<Modal show onHide={onClose} size="lg" className="ventas-nuevo-modal crm-np" aria-labelledby="np-titulo">
			<Modal.Header closeButton closeLabel="Cerrar">
				<Modal.Title id="np-titulo">Nuevo Prospecto</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className="np-tel">
					<label htmlFor="np-num">Teléfono</label>
					<div className="np-tel-fila">
						<select aria-label="Lada" value={lada} onChange={(e) => setLada(e.target.value)}>
							{LADAS.map((l) => <option key={l.k} value={l.k}>{l.t}</option>)}
							<optgroup label="Otros países">
								{OTRAS_LADAS.map((c) => <option key={"o" + c.id} value={c.prefix}>{c.name} +{c.prefix}</option>)}
							</optgroup>
						</select>
						<input id="np-num" type="tel" inputMode="tel" autoFocus autoComplete="off" value={num} onChange={(e) => setNum(e.target.value)}
							placeholder={lada === "52" ? "81 1234 5678" : lada === "504" ? "9999 9999" : "Número"} />
					</div>
					{buscando ? <div className="np-estado">Buscando…</div>
						: lookup && !lookup.valid ? <div className="np-estado oro">Ese número no parece válido.</div>
						: lookup?.valid && !m ? <div className="np-estado">{telefonoBonito(lookup.phone || telefono)} · no está en el CRM</div> : null}
					{m ? (
						<div className="np-existe">
							<div className="np-existe-tx">
								<b>Ya existe: {nombreCompleto(m)}</b>
								<span>{[m.etapa, m.status, m.owner_zoho_name || "Sin asignar", m.first_contact_at ? "desde " + cuando(m.first_contact_at) : null].filter(Boolean).join(" · ")}</span>
							</div>
							<button type="button" className="crm-b negro" onClick={() => onOpenExisting(m.id)}>Abrir Ficha</button>
						</div>
					) : null}
					{d && !m ? (
						<div className="np-detectado">
							<span className="np-tag">Detectado</span>
							<span>Escribió {d.canal ? "al " + d.canal : "por WhatsApp"} {cuando(d.at)}{d.adName ? " · anuncio «" + d.adName + "»" : ""}{d.campana && d.campana !== d.adName ? " · " + d.campana : ""}</span>
						</div>
					) : null}
				</div>

				<fieldset disabled={!abierto} className={"np-campos" + (abierto ? "" : " apagado")}>
					<div className="np-rejilla">
						<div><label htmlFor="np-nom">Nombre</label><input id="np-nom" value={f.firstName} onChange={(e) => set("firstName", e.target.value)} /></div>
						<div><label htmlFor="np-ape">Apellidos</label><input id="np-ape" value={f.lastName} onChange={(e) => set("lastName", e.target.value)} /></div>
					</div>

					<div className="np-fila2">
						<div>
							<span className="np-l" id="np-et-l">Etapa</span>
							<div className="np-pills" role="radiogroup" aria-labelledby="np-et-l">
								{ETAPAS.map((e) => {
									const on = f.etapaId === e.id, c = ETAPA_BORDE[e.id];
									return <button key={e.id} type="button" role="radio" aria-checked={on} className={"np-pill etapa" + (on ? " on" : "")} data-etapa={e.id}
										style={on ? { borderColor: c, color: c, background: c + "14" } : undefined} onClick={() => set("etapaId", e.id)}>{e.name}</button>;
								})}
							</div>
						</div>
						<div>
							<span className="np-l" id="np-st-l">Status</span>
							<div className="np-pills" role="radiogroup" aria-labelledby="np-st-l">
								{statuses.map((s) => {
									const on = f.statusId === s.id, pc = ST_PILL[s.code] || { bg: "#F4ECE6", fg: "#2B1C16" };
									return <button key={s.id} type="button" role="radio" aria-checked={on} className={"np-pill" + (on ? " on" : "")}
										style={on ? { background: pc.bg, color: pc.fg, borderColor: pc.lleno ? pc.bg : pc.fg + "55" } : undefined}
										onClick={() => set("statusId", s.id)}>
										{!on ? <i className="np-dot" style={{ background: pc.lleno ? pc.bg : pc.fg }} /> : null}{s.code === "nuevo_cliente" ? "Nuevo" : s.name}</button>;
								})}
							</div>
							{st?.bool_needs_reason ? (
								<select className="np-motivo" aria-label="Motivo de Potencial" value={f.potencialMotivo} onChange={(e) => set("potencialMotivo", e.target.value)}>
									<option value="">Motivo…</option>
									{(catalogs?.motivosPotencial || []).filter((x) => !["heredado de Zoho", "sin status en Zoho"].includes(x)).map((x) => <option key={x} value={x}>{x}</option>)}
								</select>
							) : null}
						</div>
					</div>

					<div className="np-rejilla tres">
						<div>
							<span className="np-l">Curso de interés</span>
							<div className="np-cursos"><CursosCompacto value={f.courseInterest} onChange={(v) => set("courseInterest", v)} /></div>
						</div>
						<div>
							<label htmlFor="np-pla">Plantel</label>
							<select id="np-pla" value={f.campusId} onChange={(e) => set("campusId", e.target.value)}>
								{(catalogs?.campuses || []).map((c) => <option key={c.id} value={c.id}>{c.name.replace("Washington Academy ", "")}</option>)}
							</select>
						</div>
						<div>
							<label htmlFor="np-due">Dueña</label>
							{esSup ? (
								<select id="np-due" value={f.ownerZohoId} onChange={(e) => set("ownerZohoId", e.target.value)}>
									<option value="">Yo</option>
									{(catalogs?.advisors || []).filter((a) => a.id !== catalogs?.myZohoId).map((a) => <option key={a.id} value={a.id}>{a.name}{a.team ? " · " + a.team : ""}</option>)}
									<option value="none">Sin asignar</option>
								</select>
							) : <div className="np-fijo">Tú</div>}
						</div>
					</div>

					<div>
						<span className="np-l" id="np-fu-l">Fuente {auto && f.sourceId === d?.sourceId ? <span className="np-tag">Detectado</span> : null}</span>
						<div className="np-pills" role="radiogroup" aria-labelledby="np-fu-l">
							{fuentes.map((s) => (
								<button key={s.id} type="button" role="radio" aria-checked={f.sourceId === s.id} className={"np-pill fuente" + (f.sourceId === s.id ? " on" : "")}
									onClick={() => set("sourceId", f.sourceId === s.id ? 0 : s.id)}>{s.name}</button>
							))}
						</div>
					</div>

					<div className={"np-rejilla tres" + (det ? "" : " sin-det")}>
						<div>
							<label htmlFor="np-can">Escribió al número</label>
							<select id="np-can" value={f.channelId} onChange={(e) => set("channelId", e.target.value)}>
								<option value="">—</option>
								{canales.map((c) => <option key={c.id} value={c.id}>{c.label || c.display_phone}</option>)}
							</select>
						</div>
						{det ? (
							<div>
								<label htmlFor="np-det">{det.label}</label>
								<input id="np-det" value={det.campo === "adName" ? f.adName : f.detalle} placeholder={det.ph}
									onChange={(e) => set(det.campo, e.target.value)} />
							</div>
						) : null}
						<div className="np-nota">
							<label htmlFor="np-nota">Nota</label>
							<textarea id="np-nota" rows={1} value={f.note} onChange={(e) => set("note", e.target.value)} placeholder="Qué dijo, qué pidió" />
						</div>
					</div>
				</fieldset>
				{error ? <div className="crm-aviso np-error">{error}</div> : null}
			</Modal.Body>
			<Modal.Footer>
				{abierto && falta ? <span className="np-falta">Falta {falta}</span> : null}
				<button type="button" className="crm-b" onClick={onClose}>Cancelar</button>
				<button type="button" className="crm-b negro" disabled={!!falta || busy} onClick={guardar}>{busy ? "Guardando…" : "Guardar y Abrir"}</button>
			</Modal.Footer>
		</Modal>
	);
};

export default NewProspectModal;
